import dayjs from '@/config/dayjs';

export type HistoricalValuesRowFieldError =
  | 'missingDate'
  | 'missingValue'
  | null;
export type HistoricalValuesRowError = 'duplicateDate' | null;

export interface HistoricalValuesRow {
  EffectiveDate: string;
  Balance: string;
  fieldError?: HistoricalValuesRowFieldError;
  rowError?: HistoricalValuesRowError;
}

/** Formik `errors.HistoricalValues[i]` shape (error codes map to i18n keys in the UI). */
export interface HistoricalValueRowFormikErrors {
  EffectiveDate?: 'missingDate';
  Balance?: 'missingValue';
  rowError?: 'duplicateDate';
}

const isEmptyCurrency = (value: string | undefined) => {
  return value === undefined || value.trim() === '';
};

const normalizeDateKey = (value: string) => {
  const parsed = dayjs(value, 'MM/DD/YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

export function validateHistoricalValueRows<T extends HistoricalValuesRow>(
  rows: T[],
): { next: T[]; isValid: boolean } {
  const next: T[] = rows.map((row) => ({
    ...row,
    fieldError: null,
    rowError: null,
  }));

  const dateKeyToIndices = new Map<string, number[]>();

  next.forEach((row, idx) => {
    const hasDate = Boolean(row.EffectiveDate);
    const hasValue = !isEmptyCurrency(row.Balance);

    if (hasDate && !hasValue) {
      next[idx].fieldError = 'missingValue';
    }
    if (!hasDate && hasValue) {
      next[idx].fieldError = 'missingDate';
    }

    if (hasDate && hasValue) {
      const key = normalizeDateKey(row.EffectiveDate);
      if (!key) return;
      const existing = dateKeyToIndices.get(key) ?? [];
      existing.push(idx);
      dateKeyToIndices.set(key, existing);
    }
  });

  for (const indices of dateKeyToIndices.values()) {
    if (indices.length <= 1) continue;
    indices.forEach((idx) => {
      next[idx].rowError = 'duplicateDate';
    });
  }

  const isValid = next.every((x) => !x.fieldError && !x.rowError);
  return { next, isValid };
}

/**
 * Builds Formik errors for {@link HistoricalValuesFormValues.HistoricalValues}.
 * Returns `undefined` when every row is valid (including empty placeholder rows).
 */
export function getHistoricalValuesFormErrors(
  rows: HistoricalValuesRow[],
): HistoricalValueRowFormikErrors[] | undefined {
  const { next, isValid } = validateHistoricalValueRows(rows);
  if (isValid) {
    return undefined;
  }

  return next.map((row) => {
    const rowErrors: HistoricalValueRowFormikErrors = {};
    if (row.fieldError === 'missingDate') {
      rowErrors.EffectiveDate = 'missingDate';
    }
    if (row.fieldError === 'missingValue') {
      rowErrors.Balance = 'missingValue';
    }
    if (row.rowError === 'duplicateDate') {
      rowErrors.rowError = 'duplicateDate';
    }
    return rowErrors;
  });
}
